import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/index.css";
import App from "@/App";
import { ThemeProvider } from "@/construction/ThemeContext";
import Login from "@/Login";
import Signup from "@/Signup";
import Subscription from "@/Subscription";
import { AuthProvider, useAuth } from "@/SimpleAuthContext";

/* Suppress benign ResizeObserver loop warning that CRA's dev overlay
 * surfaces as a fatal error. The browser handles the loop correctly —
 * this notification is purely noise from libraries like Recharts /
 * framer-motion that mutate sizes inside ResizeObserver callbacks. */
const RO_LOOP_RE = /ResizeObserver loop (completed with undelivered notifications|limit exceeded)/;
window.addEventListener('error', (e) => {
  if (e?.message && RO_LOOP_RE.test(e.message)) {
    e.stopImmediatePropagation();
    e.preventDefault();
  }
});
window.addEventListener('unhandledrejection', (e) => {
  const msg = e?.reason?.message || String(e?.reason || '');
  if (RO_LOOP_RE.test(msg)) {
    e.stopImmediatePropagation();
    e.preventDefault();
  }
});

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { currentUser, userProfile } = useAuth();
  
  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }
  
  // Check if trial expired and subscription not active
  if (userProfile && userProfile.subscription_status === 'expired') {
    return <Navigate to="/subscription" replace />;
  }
  
  return children;
};

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route 
              path="/subscription" 
              element={
                <ProtectedRoute>
                  <Subscription />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/" 
              element={
                <ProtectedRoute>
                  <App />
                </ProtectedRoute>
              } 
            />
          </Routes>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  </React.StrictMode>,
);
